import { connect } from 'react-redux';
import SearchResult from '../../../component/templates/SearchResult';
import { AgencyAppState } from 'src/redux/modules/store';
import { Dispatch } from 'redux';
import { UserState } from '@common/redux/reducers/UserReducer';
import { StoreSelectorState } from '@common/redux/reducers/StoreSelectorReducer';
import { SearchConditionState } from '@common/redux/reducers/SearchConditionReducer';
import { actions } from '@common/redux/modules/Actions';
import { LOADING_TYPE } from '@common/redux/reducers/LoadingStatusReducer';
import {
  negotiationListApi,
  NegotiationListApiRequest,
  NegotiationListApiResponse,
  NegotiationResultProductInfo,
} from '@common/external/NegotiationListApi';
import { SearchResultItem, SearchResultState } from '@common/redux/reducers/SearchResultReducer';
import { CommonResult, ConditionItem, MASKING } from '@common/external/Common';
import { ScrollInfo, NON_FILTER_BRANCHCONDITTION_KEYS } from '@common/Common';
import { SORRY_MESSAGE_TYPE } from '@common/redux/reducers/SorryMessageReducer';

// mapStateToPropsとmapDispatchToPropsについて
// private functionなので、jestから直接実行が不可であるが、Enzymeのshallow mapか exportすればテスト実施は可能
// しかし、既存コードかつ、クラスコンポーネントでreduxを使用するためだけのコンテナなので、テスト対象から除外します

/* istanbul ignore next */
const mapStateToProps = (appState: AgencyAppState) => {
  return {
    loadingStatus: appState.loadingStatus.searchResult,
    loginUser: appState.loginUser,
    storeSelector: appState.storeSelector,
    masterInfo: appState.masterInfo,
    searchCondition: appState.SearchCondition,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    search: (
      loginUser: UserState,
      storeSelector: StoreSelectorState,
      searchCondition: SearchConditionState,
      paging: ScrollInfo,
      sort: string
    ) => {
      updateNegotiationList(dispatch, loginUser, storeSelector, searchCondition, paging, sort);
    },
  };
};

export function updateNegotiationList(
  dispatch: Dispatch,
  loginUser: UserState,
  storeSelector: StoreSelectorState,
  searchCondition: SearchConditionState,
  paging: ScrollInfo,
  sort: string
) {
  dispatch(actions.updateLoadingStatus.started(LOADING_TYPE.SEARCH_RESULT));

  const request = makeNegotiationListApiRequest(loginUser, storeSelector, searchCondition, paging, sort);

  getNegotiationList(request)
    .then((response) => {
      const result: CommonResult = response.result;

      const resultList: SearchResultItem[] = [];
      const hit: number = response.hit ? response.hit : 0;
      const negotiationList = response.negotiationResult;
      if (negotiationList) {
        for (let i = 0; i < negotiationList.length; i++) {
          const item: SearchResultItem = Object.assign(negotiationList[i], { key: 'No.' + i });
          // sort by productId (descending)
          const negotiationResultProductInfo = Array.isArray(item.negotiationResultProductInfo)
            ? [...item.negotiationResultProductInfo].sort(
                (prevItem: NegotiationResultProductInfo, nextItem: NegotiationResultProductInfo) => {
                  return nextItem.productId - prevItem.productId;
                }
              )
            : undefined;
          resultList.push({
            ...item,
            negotiationResultProductInfo,
          });
        }
      }
      const ret: SearchResultState = {
        hit,
        searchResultList: resultList,
      };

      dispatch(actions.updateSearchResult(ret));
      dispatch(actions.updateLoadingStatus.done({ params: LOADING_TYPE.SEARCH_RESULT, result }));
    })
    .catch((error) => {
      console.log(error);
      dispatch(actions.updateSorryMessage(SORRY_MESSAGE_TYPE.SYSTEM_ERROR));
      dispatch(actions.updateLoadingStatus.failed({ params: LOADING_TYPE.SEARCH_RESULT, error }));
    });
}

async function getNegotiationList(request: NegotiationListApiRequest): Promise<NegotiationListApiResponse> {
  const response: NegotiationListApiResponse = await negotiationListApi(request);

  return response;
}

function makeNegotiationListApiRequest(
  loginUser: UserState,
  storeSelector: StoreSelectorState,
  searchConditions: SearchConditionState,
  paging: ScrollInfo,
  sort: string
): NegotiationListApiRequest {
  const searchBranch: ConditionItem[] = [];
  if (searchConditions) {
    searchConditions.forEach((searchCondition) => {
      if (!searchCondition.specifiedCondition.length) return;
      const searchConditionName = searchCondition.searchConditionName;
      const searchConditionGroupId = searchCondition.searchConditionGroupId;
      const specifiedCondition = searchCondition.specifiedCondition;
      const isUsedInFilter = searchCondition.isUsedInFilter;
      const isNotActionTypeWithoutInput = !isActionTypeWithNoInputCondition(searchConditionName, specifiedCondition);
      // TODO 絞り込み条件 臨時対応
      //      negotiationProgressType と negotiationResultTypeのマッピングを合わせる
      if (searchConditionName === 'negotiationProgressType') {
        searchBranch.push({
          branchGroupId: searchConditionGroupId ? searchConditionGroupId : 0,
          branchConditionName: 'negotiationResultType',
          // specifiedConditon Convertingされた配列が設定される
          specifiedCondition: convertingNegotiationResultType(specifiedCondition),
        });
        return;
      }

      // 絞り込みフラグ有の場合、グループ99を構成する
      if (
        isUsedInFilter &&
        searchConditionGroupId == null &&
        !NON_FILTER_BRANCHCONDITTION_KEYS.includes(searchConditionName) &&
        isNotActionTypeWithoutInput
      ) {
        // 絞り込み条件画面から遷移する場合、グループ99を構成する
        searchBranch.push({
          branchGroupId: 99,
          branchConditionName: searchConditionName,
          specifiedCondition: specifiedCondition,
        });
        return;
      }

      // ダッシュボード/レポート画面から遷移する場合、グループ99を構成しない
      if (isNotActionTypeWithoutInput) {
        searchBranch.push({
          branchGroupId: searchConditionGroupId ? searchConditionGroupId : 0,
          branchConditionName: searchConditionName,
          specifiedCondition: specifiedCondition,
        });
      }
    });
  }

  const agencyCd = Object.values(storeSelector?.stores || storeSelector?.agencies || {})
    .filter((store) => store.selectedForFilter)
    .map((store) => store.agencyCd);

  const req: NegotiationListApiRequest = {
    userId: loginUser.crewCd,
    agencyCd,
    maskingFlg: MASKING.DISABLE,
    searchBranch: searchBranch,
  };
  if (paging) {
    req.scrollInfo = paging;
  }
  if (sort) {
    req.sort = sort;
  }

  return req;
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchResult);

// TODO 削除予定
function convertingNegotiationResultType(prevSpecifiedconditions: (string | null)[]): string[] {
  const convertSpecifiedcondition: string[] = [];

  prevSpecifiedconditions.forEach((item) => {
    convertSpecifiedcondition.push(convertCode(item));
  });

  return convertSpecifiedcondition;
}

function convertCode(item: string | null): string {
  switch (item) {
    case '01':
      return '07';
    case '02':
      return '01';
    case '03':
      return '02';
    case '04':
      return '05';
    case '05':
      return '03';
    default:
      return '00';
  }
}

export function isActionTypeWithNoInputCondition(searchConditionName: string, specifiedCondition: (string | null)[]) {
  return searchConditionName === 'actionType' && specifiedCondition[0] === '00';
}
